import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";

// Assuming these are your custom components, updated for MUI v5 if necessary
import WithLoading from "../../../../utils/WithLoading";
import DefaultAlert from "../../../../components/alerts/DefaultAlert";
import CardCommon from "../../../../components/card/CardCommon";
import ButtonCommon from "../../../../components/buttons/ButtonCommon";
import { updateResetHour } from "../../../../services/insightApp/metrics";
import { CustomTheme } from "../../../../types/customTheme"; // Assuming this is for theme type
import { createStyles, Grid, TextField, Typography } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import { makeStyles } from "@material-ui/styles";

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    autoComplete: {
      marginTop: "4px",
      "& .MuiAutocomplete-input": {
        color: theme.palette.custom.orange.contrastText,
      },
      "& .MuiInputBase-root": {
        color: "inherit",
        backgroundColor: theme.palette.background.entity_background,
        borderRadius: 10,
      },
      [`& fieldset`]: {
        borderRadius: 10,
        border: "none",
        color: theme.palette.custom.orange.contrastText,
      },
    },
    titleColor: {
      color: theme.palette.custom.orange.contrastText,
    },
  }),
);

// --- Type Definitions ---
interface HourOption {
  id: number;
  hour: string;
}

interface AlertConfigNodeProps {
  isLoading: boolean;
  hours: number; // Assuming `hours` is a number from the parent
}

interface NotificationState {
  open: boolean;
  message: string;
  severity: "success" | "error";
}

// --- Helper & Constant Generation ---
/**
 * Generates an array of hour options for the dropdown.
 * @returns {HourOption[]} An array of hour objects.
 */
const generateHourOptions = (): HourOption[] => {
  return Array.from({ length: 24 }, (_, i) => ({
    id: i,
    hour: `${String(i).padStart(2, "0")}:00`,
  }));
};

/**
 * Finds the hour option object for a given integer hour.
 * @param {number} hour The hour as an integer (0-23).
 * @returns {HourOption | undefined} The corresponding hour option or undefined if not found.
 */
const getHourOptionById = (
  hour: number,
  options: HourOption[],
): HourOption | undefined => {
  return options.find((option) => option.id === hour);
};

// --- Component ---
const AlertConfigNode: React.FC<AlertConfigNodeProps> = ({
  isLoading,
  hours,
}) => {
  // Use `useParams` from React Router v6
  const { locationId } = useParams<{ locationId: string }>();

  // State Management
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [selectedHour, setSelectedHour] = useState<any>(null);
  const [initialHour, setInitialHour] = useState<any>(null);
  const [notification, setNotification] = useState<any>({
    open: false,
    message: "",
    severity: "success",
  });

  // Memoize the options array to prevent re-generation on every render
  const hoursOption = useMemo(() => generateHourOptions(), []);

  // Effect to set the initial and selected hour when props change
  useEffect(() => {
    if (typeof hours === "number" && hours >= 0 && hours <= 23) {
      const hourObj = getHourOptionById(hours, hoursOption);
      if (hourObj) {
        setSelectedHour(hourObj);
        setInitialHour(hourObj);
      }
    }
  }, [hours, hoursOption]);

  // Memoized event handlers
  const handleChangeHour = useCallback(
    (event: any, value: HourOption | null) => {
      setSelectedHour(value);
    },
    [],
  );

  const handleSave = useCallback(async () => {
    if (!selectedHour || !locationId) return;

    setIsButtonLoading(true);
    try {
      await updateResetHour(locationId, { hour: selectedHour.id });
      setInitialHour(selectedHour);
      setNotification({
        open: true,
        message: "Hour updated successfully!",
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to update reset hour:", error);
      setNotification({
        open: true,
        message: "Failed to update. Please try again later.",
        severity: "error",
      });
    } finally {
      setIsButtonLoading(false);
    }
  }, [selectedHour, locationId]);

  const handleCloseAlert = () => {
    setNotification((prev: any) => ({ ...prev, open: false }));
  };

  const classes = useStyles();
  const hasChanges = initialHour?.id !== selectedHour?.id;

  return (
    <>
      <Grid container>
        <Grid item xs={12} sm={6} md={4}>
          <CardCommon style={{ margin: "8px 0", padding: "20px" }}>
            <Typography variant="h5" gutterBottom>
              Set Hour
            </Typography>

            <Autocomplete
              options={hoursOption}
              value={selectedHour}
              onChange={handleChangeHour}
              getOptionLabel={(option) => option.hour}
              disableClearable
              size="small"
              popupIcon={<ArrowDropDownIcon />}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Hour"
                  variant="outlined"
                />
              )}
              // Use the sx prop for styling (MUI v5)
                   classes={{ root: classes.autoComplete }}
            />

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "16px",
              }}
            >
              <ButtonCommon
                variant="contained"
                onClick={handleSave}
                isLoadingPage={isButtonLoading}
                disabled={!hasChanges || isButtonLoading}
              >
                Save
              </ButtonCommon>
            </div>
          </CardCommon>
        </Grid>
      </Grid>

      <DefaultAlert
        open={notification.open}
        handleClose={handleCloseAlert}
        message={notification.message}
        severity={notification.severity}
      />
    </>
  );
};

export default WithLoading(AlertConfigNode);
